import {
  Appliance,
  Group,
  LocalisedText,
  Measurement,
  MeasurementType,
  OptionalTuple,
  ProjectPartString,
  UnitOfMeasurement,
} from '@/types/helpers';
import { Enum, EnumItem, ModuleOptions, ModuleSelection, Module, Row } from '@/api/airchef/Api';

import { generateColor } from '@marko19907/string-to-color';
import { InvalidFileError } from './errors';
import { tryGetMeasurementValue } from './planningUtils';
import { convertMeasurement } from './measurements';

export const isMac = navigator.userAgent.includes('Mac OS') || navigator.userAgent.includes('Macintosh');

export const supportsInterpolateSize = CSS.supports('interpolate-size', 'allow-keywords');

export function getRandomString(length = 4, separator = ''): string {
  const arr = new Uint16Array(length);

  crypto.getRandomValues(arr);

  return [...arr].map((x) => x.toString(16).padStart(4, '0')).join(separator);
}

export function generateRoomPartId<
  T extends ProjectPartString | 'row' | 'comment' | 'ceiling-exhaust-plenum' | 'ceiling-plenum-filter',
>(): string & { __type: T };
export function generateRoomPartId<_T extends 'none'>(): string;
export function generateRoomPartId<
  T extends ProjectPartString | 'row' | 'comment' | 'ceiling-exhaust-plenum' | 'ceiling-plenum-filter',
>(): string & { __type: T } {
  return crypto.randomUUID() as string & { __type: T };
}

export function getRandomStringSimple(length = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890';

  const numbers = crypto.getRandomValues(new Uint8Array(length));

  let res = '';
  for (let i = 0; i < length; i++) {
    res += chars.charAt(numbers[i] % chars.length);
  }

  return res;
}

export function generateId() {
  return `${getRandomStringSimple(4)}-${getRandomStringSimple(8)}`;
}

export function isUndo(e: React.KeyboardEvent<HTMLElement>) {
  return isCommandHotkey(e, 'z');
}

export function isCommandHotkey(e: React.KeyboardEvent<HTMLElement>, key: string) {
  if (isMac) {
    return e.key === key && e.metaKey;
  }
  return e.key === key && e.ctrlKey;
}

export function getLocale(): string {
  return navigator.languages && navigator.languages.length > 0 ? navigator.languages[0] : navigator.language;
}

export function isUs(): boolean {
  return getLocale().toLocaleLowerCase() === 'en-us';
}

export async function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = (ev) => {
      reject(ev);
    };

    reader.onloadend = () => {
      const { result } = reader;
      if (typeof result === 'string' || result instanceof String) {
        resolve(result as string);
      }
      reject(result);
    };
    reader.readAsText(file);
  });
}

export function getCurrentTimestamp(compatibility = true) {
  // Timezone name extension to the standard is currently in the proposed stage
  // as of Sep 2025: https://www.rfc-editor.org/rfc/rfc9557.html
  // And is not supported beyond the Temporal & Java APIs.
  // So, to make things easier with the backend, skip the time zone name for now.
  return Temporal.Now.zonedDateTimeISO().toString({ timeZoneName: compatibility ? 'never' : undefined });
}

export function formatTimestamp(timestamp: string, dateOnly?: boolean): string {
  if (dateOnly) {
    const date = Temporal.PlainDate.from(timestamp);
    return date.toLocaleString(getLocale(), {
      dateStyle: 'short',
    });
  }
  // Can't parse directly as zonedDateTime, as the source date is missing the timeZoneName.
  // See getCurrentTimestamp() above.
  const instant = Temporal.Instant.from(timestamp);
  const timeZone = Temporal.Now.timeZoneId();
  const date = instant.toZonedDateTimeISO(timeZone);
  return date.toLocaleString(getLocale(), {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function getSafeTimestamp() {
  const now = getCurrentTimestamp();
  const instant = Temporal.Instant.from(now);
  const tz = Temporal.Now.timeZoneId();
  const date = instant.toZonedDateTimeISO(tz);

  const year = String(date.year).padStart(4, '0');
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  const hour = String(date.hour).padStart(2, '0');
  const minute = String(date.minute).padStart(2, '0');

  return `${year}-${month}-${day}_${hour}-${minute}`;
}

export function formatNumber(n: number, min = 0, max = 2): string {
  const locale = getLocale();
  const formatter = new Intl.NumberFormat(locale, {
    maximumFractionDigits: max,
    minimumFractionDigits: min,
  });

  return formatter.format(n);
}

export function getDisplayText(text: LocalisedText, defaultText = '', langOverride?: string): string {
  if (!text) {
    return defaultText;
  }
  if (typeof text === 'string') return text;
  const locales = Object.keys(text);
  if (locales.length === 0) {
    return defaultText;
  }

  // Get current locale from browser
  const locale = langOverride ?? getLocale();

  // Return text matching user locale if available
  if (text[locale]) {
    return text[locale] as string;
  }

  // Try finding a language without matching the possible locale part
  const lang = locale.includes('-') ? locale.split('-')[0] : locale;

  // Check if there's a plain language version e.g. en instead of en-GB.
  if (text[lang]) {
    return text[lang] as string;
  }

  // Find other variants of the language, e.g. en-GB instead of en-US.
  const similarLang = locales.find((loc) => loc.startsWith(lang));
  if (similarLang && text[similarLang]) {
    return text[similarLang] as string;
  }

  // Default to english if possible
  const enText = (text['en-US'] ?? text['en-GB'] ?? text['en'] ?? '') as string;
  if (enText) {
    return enText;
  }

  // Just return whatever the first language is
  return text[locales[0]];
}

export function assertDeprecated(reason: string, description?: string) {
  let msg = `### DEPRECATED ### - ${reason}`;

  if (description !== undefined) {
    msg += `\n${description}\n### DEPRECATED ###`;
  }

  console.warn(msg);
}

function isEnumItem(obj: unknown): obj is EnumItem {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  if (!('value' in obj) || obj.value === undefined || typeof obj.value !== 'string') {
    return false;
  }

  if (!('label' in obj) || typeof obj.label !== 'object' || obj.label === null) {
    return false;
  }

  if (Object.keys(obj.label).length === 0) {
    return false;
  }

  if (Object.entries(obj.label).some(([lang, lab]) => typeof lang !== 'string' || typeof lab !== 'string')) {
    return false;
  }

  return true;
}

export function isEnum(obj: unknown): obj is Enum {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }

  if (!('value' in obj) || !isEnumItem(obj.value)) {
    return false;
  }

  if (!('enum' in obj) || typeof obj.enum !== 'object' || !Array.isArray(obj.enum)) {
    return false;
  }

  if (obj.enum.some((i: unknown) => !isEnumItem(i))) {
    return false;
  }

  return true;
}

export function isModule(obj: unknown): obj is Module {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }

  if (!('id' in obj) || typeof obj.id !== 'string') return false;
  if (!('type' in obj) || typeof obj.type !== 'string') return false;
  if (!('airchefValues' in obj) || typeof obj.airchefValues !== 'object' || obj.airchefValues === null) return false;
  if (!('name' in obj.airchefValues) || typeof obj.airchefValues.name !== 'string') return false;

  return true;
}

function isModuleSelection(obj: unknown): obj is ModuleSelection {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }

  if (!('module' in obj) || !isModule(obj.module)) return false;

  if ('locked' in obj && obj.locked !== undefined && obj.locked !== null && typeof obj.locked !== 'boolean')
    return false;

  return true;
}

export function isModuleOptions(obj: unknown): obj is ModuleOptions {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }

  if (!('modulesSelected' in obj) || !Array.isArray(obj.modulesSelected)) return false;

  if (obj.modulesSelected.some((ms: unknown) => !isModuleSelection(ms))) return false;

  return true;
}

export async function generateHash(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  const arr = Array.from(new Uint8Array(hashBuffer));
  const hex = arr.map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return hex;
}

export function groupApplianceLogger(
  msg: string,
  data: Group | Row | OptionalTuple<Appliance[]>,
  extraStyles: string[] = [],
) {
  function getIdStyle(id: string) {
    const style = ['color: white', `background-color: ${generateColor(id, { lightness: 30 })}`].join(';');

    return style;
  }
  const styles: string[] = extraStyles;
  const plainStyle = 'color:#ddd,background-color:transparent;';

  let applianceIdsByRow = '';
  let out = '';
  if (Array.isArray(data)) {
    applianceIdsByRow = data
      .map((row) =>
        (row || [])
          .map((app) => {
            const shortId = app.id.split('-')[0];
            styles.push(getIdStyle(shortId));
            styles.push(plainStyle);
            return `%c${shortId}%c`;
          })
          .join(', '),
      )
      .join(' | ');
  } else {
    const rows = data.type === 'row' ? [data] : data.rows;
    applianceIdsByRow = rows
      .map((row) =>
        row.relations.applianceIds
          .map((aid) => {
            const shortId = aid.split('-')[0];
            styles.push(getIdStyle(shortId));
            styles.push(plainStyle);
            return `%c${shortId}%c`;
          })
          .join(', '),
      )
      .join(' | ');
  }
  out = `[${Array.isArray(data) ? 'arr' : data.type}] ${msg}, byRow: [${applianceIdsByRow}%c]`;
  styles.push(plainStyle);

  if (!Array.isArray(data) && data.type === 'group') {
    styles.push(plainStyle);
    const groupAppliances = data.relations.applianceIds
      .map((aid) => {
        const shortId = aid.split('-')[0];
        styles.push(getIdStyle(shortId));
        styles.push(plainStyle);
        return `%c${shortId}%c`;
      })
      .join(', ');
    out += `%c, byGroup: [${groupAppliances}%c]`;
    styles.push(plainStyle);
  }

  console.log(`${out}`, ...styles);
}

/**
 * Cloning performance tested in Firefox by
 * cloning a project with two populated rooms.
 * - Structured clone took 22118ms for 10000 runs (baseline: +0.00%)
 * - Lodash Clone deep took 46887ms for 10000 runs (+111.99% vs baseline)
 * - JSON parse took 17396ms for 10000 runs (-21.35% vs baseline)
 *
 * Chrome had 35% improvement with simpleClone over structuredClone.
 * @param data
 * @returns
 */
export function simpleClone<T>(data: T): T {
  if (data === undefined) return undefined as T;
  try {
    return JSON.parse(JSON.stringify(data)) as T;
  } catch (err) {
    console.error('Clone failed', err);
    console.log('data', data, JSON.stringify(data));
    return structuredClone(data) as T;
  }
}

export async function cloneBenchmark<T>(data: T, iterations = 5000) {
  const getDifferenceToBaseline = (duration: number, baseline: number) => {
    if (baseline === 0) {
      return 'N/A';
    }

    const difference = ((duration - baseline) / baseline) * 100;
    const sign = difference >= 0 ? '+' : '';

    return `${sign}${difference.toFixed(2)}%`;
  };

  console.log('Running benchmark...');

  const startStructured = Date.now();

  for (let i = 0; i < iterations; i++) {
    structuredClone(data);
  }

  const durationStructured = Date.now() - startStructured;
  console.log(
    `structuredClone took ${durationStructured}ms with ${iterations} (${(durationStructured / iterations).toFixed(2)}ms per iteration) (baseline: +0.00%)`,
  );

  const startSimple = Date.now();

  for (let i = 0; i < iterations; i++) {
    simpleClone(data);
  }

  const durationSimple = Date.now() - startSimple;
  console.log(
    `simpleClone took ${durationSimple}ms with ${iterations} (${(durationSimple / iterations).toFixed(2)}ms per iteration) (${getDifferenceToBaseline(durationSimple, durationStructured)} vs baseline)`,
  );

  console.log('Done');
}

const knownMimeTypes = {
  pdf: 'application/pdf',
  png: 'image/png',
};

export function downloadFile(filename: string, base64content: string, fileType?: keyof typeof knownMimeTypes) {
  if (!base64content.startsWith('data:')) {
    if (!fileType) {
      throw new InvalidFileError('Base64 encoded file data is not a data url, and fileType is missing.');
    }
    base64content = `data:${knownMimeTypes[fileType]};base64,` + base64content;
  }

  const link = window.document.createElement('a');

  link.style.display = 'block';
  link.style.width = '0px';
  link.style.height = '0px';
  link.href = base64content;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
  }, 0);
}

export function formatMeasurement<T extends MeasurementType>(
  measurement: Measurement<T>,
  targetUnit?: UnitOfMeasurement<T>,
  minDecimals = 0,
  maxDecimals = 2,
  hideUnit = false,
) {
  if (!measurement && !targetUnit) return null;

  let value: number = 0;
  if (measurement?.type === 'other') {
    value = measurement.value;
  } else {
    if (targetUnit) {
      value = tryGetMeasurementValue(measurement, targetUnit);
    } else if (measurement && !targetUnit) {
      value = convertMeasurement(measurement, measurement.unit).value;
    }
  }

  const unit = targetUnit ?? measurement?.unit;

  const locale = getLocale();
  const formatter = new Intl.NumberFormat(locale, {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: minDecimals,
  });

  if (hideUnit) {
    return formatter.format(value);
  }

  return `${formatter.format(value)} ${unit}`;
}

export function formatUnit(unit: string): string {
  if (unit === '') return '';
  const sScripts = unit
    .replace('m2', 'm\u00B2')
    .replace('ft2', 'ft\u00B2')
    .replace('ft3', 'ft\u00B3')
    .replace('m3', 'm\u00B3')
    .replace('m^3', 'm\u00B3')
    .replace('m^2', 'm\u00B2')
    .replace('ft^2', 'ft\u00B2')
    .replace('ft^3', 'ft\u00B3')
    .replace('inH2O', 'inH\u2082O');
  const parts = sScripts.split('/');
  if (parts.length > 3 || parts.length < 2) {
    return `<math display="inline"><mn>${sScripts.replace(/\//g, '</mn><mo>/</mo><mn>')}</mn></math>`;
  }
  if (parts.length === 3) {
    return `<math display="inline"><mfrac><mrow><mn>${parts[0]}</mn><mo>/</mo><mn>${parts[1]}</mn></mrow><mrow><mn>${parts[2]}</mn></mrow></mfrac></math>`;
  }
  return `<math display="inline"><mfrac><mn>${parts[0]}</mn><mn>${parts[1]}</mn></mfrac></math>`;
}

export async function hashString(str: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);

  const hash = await crypto.subtle.digest('SHA-256', data);

  const hashString = new Uint8Array(hash).toHex();

  return hashString;
}
